import { useState, useEffect, useContext, useMemo } from 'react';
import { Box } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppContext, SttFullDialog, SttTabs } from '../../../sporttia/all';
import ActivityRates from './ActivityRates';
import ActivityClasses from './ActivityClasses';
import ActivityStudents from './ActivityStudents';
import ActivityForm from './ActivityForm';
import ActivityDevices from './ActivityDevices';
import ActivityDetails from './ActivityDetails';
import { useCrud } from '../../../lib/hooks';
import ActivityDocuments from './ActivityDocuments';
import ActivityRelocations from './ActivityRelocations';
import type { Activity as ActivityType } from '../../../types/models/Activity';

type Props = {
    id: number | string | null;
    trash: boolean;
    onChange: (activity?: {
        id: number | string | null;
        trash: boolean;
    }) => void;
    onClose: () => void;
    disableUrlPath?: boolean;
    initialTabKey?: string;
};

type ActivityLocal = {
    id?: number;
    name?: string;
    description?: string;
    status?: string | null;
    type?: string | null;
    idSport?: number | null;
    idTeacher?: number | null;
    maxStudents?: number;
    inscIni?: string;
    inscEnd?: string;
    ini?: string;
    end?: string;
    rates?: unknown[];
    form?: ActivityFormQuestion[];
    /** Only present, as true, on a copy duplicated relocating its students. */
    relocateStudents?: boolean;
};

type ActivityFormQuestion = {
    id: number;
    name: string;
    typeLang: string;
    mandatory: boolean;
};

type TeacherLocal = {
    id: number;
    name: string;
    fullName: string;
};

export default function Activity({
    id,
    trash,
    onChange,
    onClose,
    disableUrlPath,
    initialTabKey,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [activity, setActivity, , Get] = useCrud<ActivityLocal>(
        undefined as unknown as ActivityLocal,
    );
    const [teachers, setTeachers] = useState<TeacherLocal[]>([]);

    function loadTeachers(): Promise<void> {
        return Get(`/scs/${cxt?.sc?.id}/teachers`, {
            page: 1,
            rows: 1000,
        }).then((response: unknown) => {
            const typedResponse = response as { rows?: TeacherLocal[] };
            if (typedResponse.rows) {
                setTeachers(typedResponse.rows);
            }
        });
    }

    function loadActivity(): Promise<void> {
        return Get(`/activities/${id}`, { trash }).then((response: unknown) => {
            const typedResponse = response as { activity?: ActivityLocal };
            if (typedResponse.activity) {
                setActivity(typedResponse.activity);
            }
        });
    }

    function loadRates(idActivity?: number): void {
        const idParam = activity?.id || idActivity || id;
        cxt?.api('GET', `/activities/${idParam}/rates`, {
            success: (response: { activityRates?: unknown[] }) => {
                if (response.activityRates) {
                    setActivity({
                        ...activity,
                        rates: response.activityRates,
                    });
                }
            },
        });
    }

    useEffect(() => {
        loadTeachers();
        // load
        if (!Number.isNaN(parseInt(id as string))) {
            Promise.all([loadActivity()]).finally(() => {});
        }
        // creating
        else if (id === 'create') {
            const now = new Date();
            setActivity({
                name: '',
                description: '',
                status: null,
                type: null,
                idSport: null,
                idTeacher: null,
                maxStudents: undefined,
                inscIni: format(now, 'yyyy-MM-dd 07:00:00'),
                inscEnd: format(now, 'yyyy-12-31 23:59:59'),
                ini: format(now, 'yyyy-MM-dd 00:00:00'),
                end: format(now, 'yyyy-12-31 23:59:59'),
            });
        }
        // offload
        else {
            setActivity(undefined as unknown as ActivityLocal);
            setTeachers([]);
        }
    }, [id]);

    useEffect(() => {
        if (activity && activity.id && !activity.rates) {
            loadRates(activity.id);
        }
    }, [activity]);

    // Define tabs configuration with keys for URL routing
    // Must be before early return to maintain hooks order
    const tabsConfig = useMemo(
        () => [
            { key: 'detail', show: true },
            { key: 'rates', show: !!activity?.id },
            { key: 'classes', show: !!activity?.id },
            { key: 'students', show: !!activity?.id },
            { key: 'form', show: !!activity?.id },
            { key: 'devices', show: !!activity?.id },
            { key: 'documents', show: !!activity?.id },
            {
                key: 'relocations',
                show: !!activity?.id && !!activity?.relocateStudents,
            },
        ],
        [activity?.id, activity?.relocateStudents],
    );

    // Get only visible tab keys
    const visibleTabKeys = useMemo(
        () => tabsConfig.filter((tab) => tab.show).map((tab) => tab.key),
        [tabsConfig],
    );

    // Calculate initial tab index from key
    const computedInitialTab = useMemo(() => {
        if (initialTabKey) {
            const index = visibleTabKeys.indexOf(initialTabKey);
            return index >= 0 ? index : 0;
        }
        return 0;
    }, [initialTabKey, visibleTabKeys]);

    if (!activity) {
        return null;
    }

    // Handle tab change - update URL
    const handleTabChange = (tabIndex: number) => {
        const tabKey = visibleTabKeys[tabIndex];
        if (tabKey && activity.id && !disableUrlPath) {
            navigate(`/activities/${activity.id}/${tabKey}`, { replace: true });
        }
    };

    // Tabs
    const tabs = [
        {
            key: 'detail',
            caption: t('Detail'),
            component: (
                <ActivityDetails
                    item={activity}
                    teachers={teachers}
                    onSave={(activityToSave: ActivityLocal) => {
                        setActivity(activityToSave);
                        onChange(
                            activityToSave as {
                                id: number | string | null;
                                trash: boolean;
                            },
                        );
                    }}
                    onDelete={(activityToDelete: ActivityLocal) => {
                        onChange(
                            activityToDelete as {
                                id: number | string | null;
                                trash: boolean;
                            },
                        );
                        onClose();
                    }}
                />
            ),
        },
        {
            key: 'rates',
            show: !!activity.id,
            caption: t('Rates'),
            component: (
                <ActivityRates
                    item={activity as unknown as ActivityType}
                    onSave={loadRates}
                />
            ),
        },
        {
            key: 'classes',
            show: !!activity.id,
            caption: t('Classes'),
            component: (
                <ActivityClasses
                    teachers={
                        teachers as unknown as { id: number; name: string }[]
                    }
                    item={activity as unknown as ActivityType}
                />
            ),
        },
        {
            key: 'students',
            show: !!activity.id,
            caption: t('Students'),
            component: (
                <ActivityStudents
                    item={activity as any}
                    onSave={() => {
                        onChange();
                    }}
                    onPayment={() => {
                        onChange();
                    }}
                    onGenerateFee={() => {
                        onChange();
                    }}
                />
            ),
        },
        {
            key: 'form',
            show: !!activity.id,
            caption: t('Form'),
            component: (
                <ActivityForm
                    onChange={loadActivity}
                    item={activity as unknown as ActivityType}
                />
            ),
        },
        {
            key: 'devices',
            show: !!activity.id,
            caption: t('Devices'),
            component: (
                <ActivityDevices item={activity as unknown as ActivityType} />
            ),
        },
        {
            key: 'documents',
            show: !!activity.id,
            caption: t('Documents'),
            component: (
                <ActivityDocuments
                    activity={activity as unknown as ActivityType}
                />
            ),
        },
        {
            key: 'relocations',
            show: !!activity.id && !!activity.relocateStudents,
            caption: t('activity.relocation.tab'),
            component: (
                <ActivityRelocations
                    item={
                        activity as {
                            id?: number;
                            ini?: string;
                            rates?: {
                                id: number;
                                name: string;
                                enrollment?: number;
                                trash?: boolean;
                            }[];
                        }
                    }
                />
            ),
        },
    ];

    return (
        <SttFullDialog
            open
            onClose={onClose}
            title={id === 'create' ? t('CreateActivity') : activity.name}
            urlPath={
                disableUrlPath || !id || id === 'create'
                    ? undefined
                    : `/activities/${id}`
            }
        >
            <Box p={3}>
                <SttTabs
                    tabs={tabs}
                    initialTab={computedInitialTab}
                    onTabChange={handleTabChange}
                />
            </Box>
        </SttFullDialog>
    );
}
